import React, { useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, CheckCircle2, TrendingUp, Users } from 'lucide-react';
import { stats, services, testimonials, clients, whyMePoints } from '../mock';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';

const Home = () => {
  const heroImageUrl = '/images/portrait-hero.jpg';

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Stats carousel
  const [statsEmblaRef, statsEmblaApi] = useEmblaCarousel(
    { loop: true, align: 'start', slidesToScroll: 1 },
    [Autoplay({ delay: 3000, stopOnInteraction: false })]
  );

  // Clients carousel
  const [clientsEmblaRef, clientsEmblaApi] = useEmblaCarousel(
    { loop: true, align: 'start' },
    [Autoplay({ delay: 3000, stopOnInteraction: false })]
  );

  // Testimonials carousel
  const [testimonialsEmblaRef, testimonialsEmblaApi] = useEmblaCarousel(
    { loop: true, align: 'start' },
    [Autoplay({ delay: 5000, stopOnInteraction: false })]
  );

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 lg:px-8 bg-gradient-to-br from-amber-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left: Text + CTA */}
            <div className="space-y-6">
              <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-200">
                B2B SaaS Content & Growth
              </Badge>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight">
                SEO Content Writer and LinkedIn Ghostwriter for{' '}
                <span className="text-amber-600">B2B Brands and CxOs</span>.
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                Driving organic growth for brands and founders via high impact content.
              </p>
              <div className="flex flex-wrap gap-4 pt-4">
                <Button
                  size="lg"
                  onClick={scrollToContact}
                  className="bg-amber-600 hover:bg-amber-700 text-white shadow-lg hover:shadow-xl transition-all"
                >
                  Let's Talk <ArrowRight className="ml-2" size={20} />
                </Button>
                <Link to="/portfolio">
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-2 border-gray-300 hover:border-amber-600 hover:text-amber-600 transition-all"
                  >
                    Check Portfolio
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right: Portrait */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={heroImageUrl}
                  alt="Pratish Jain"
                  className="w-full h-auto object-cover"
                />
              </div>
              {/* Decorative Element */}
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-amber-200 rounded-full blur-3xl opacity-50 -z-10"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Numbers Band - Carousel on mobile */}
      <section className="py-16 bg-white border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Desktop: Grid */}
          <div className="hidden md:grid grid-cols-5 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-amber-600 mb-2">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Mobile: Carousel */}
          <div className="md:hidden overflow-hidden" ref={statsEmblaRef}>
            <div className="flex">
              {stats.map((stat, index) => (
                <div key={index} className="flex-[0_0_100%] min-w-0 px-4">
                  <div className="text-center">
                    <div className="text-5xl font-bold text-amber-600 mb-2">
                      {stat.value}
                    </div>
                    <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Client Logo Band - Carousel on mobile */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h3 className="text-center text-gray-500 font-medium mb-10">Trusted by Leading Brands</h3>
          
          {/* Desktop: Max 2 rows with horizontal scroll if needed */}
          <div className="hidden md:block">
            <div className="grid grid-cols-4 lg:grid-cols-7 gap-6 max-w-6xl mx-auto">
              {clients.map((client) => (
                <div
                  key={client.id}
                  className="px-4 py-3 bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow flex items-center justify-center"
                  style={{ minHeight: '70px' }}
                >
                  <img
                    src={client.logo}
                    alt={client.name}
                    className="max-w-full max-h-full object-contain"
                    style={{ maxWidth: '120px', maxHeight: '50px' }}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Mobile: Carousel showing 2.5 logos at a time */}
          <div className="md:hidden overflow-hidden" ref={clientsEmblaRef}>
            <div className="flex gap-4">
              {clients.map((client) => (
                <div key={client.id} className="flex-[0_0_40%] min-w-0">
                  <div className="px-3 py-3 bg-white rounded-lg border border-gray-200 shadow-sm flex items-center justify-center" style={{ height: '70px' }}>
                    <img
                      src={client.logo}
                      alt={client.name}
                      className="max-w-full max-h-full object-contain"
                      style={{ maxWidth: '100px', maxHeight: '50px' }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-amber-100 text-amber-700 mb-4">What I Do</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Services</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Revenue-focused content services designed to turn organic traffic into qualified pipeline.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service) => (
              <Card key={service.id} className="border-2 hover:border-amber-200 hover:shadow-lg transition-all">
                <CardHeader>
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-4">
                    <TrendingUp className="text-amber-600" size={24} />
                  </div>
                  <CardTitle className="text-2xl">{service.title}</CardTitle>
                  <CardDescription className="text-base">{service.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <CheckCircle2 className="text-amber-600 mt-1 flex-shrink-0" size={18} />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Me Section - 2 columns on mobile */}
      <section className="py-20 bg-gradient-to-br from-amber-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why SaaS Teams Work With Me</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              I'm not just a content writer. I'm a growth partner who understands B2B SaaS marketing.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
            {whyMePoints.map((point, index) => (
              <Card key={index} className="border-2 hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-lg md:text-xl">{point.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm md:text-base text-gray-600">{point.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section - Carousel */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-amber-100 text-amber-700 mb-4">Client Testimonials</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What Clients Say</h2>
          </div>

          {/* Desktop: Grid */}
          <div className="hidden md:grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-2 hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Users className="text-amber-600" size={24} />
                    </div>
                    <div>
                      <p className="text-gray-700 mb-4 italic">"{testimonial.content}"</p>
                      <div>
                        <div className="font-bold text-gray-900">{testimonial.name}</div>
                        <div className="text-sm text-gray-600">{testimonial.role}</div>
                        {testimonial.company && (
                          <div className="text-sm text-amber-600 font-medium">{testimonial.company}</div>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Mobile: Carousel */}
          <div className="md:hidden overflow-hidden" ref={testimonialsEmblaRef}>
            <div className="flex">
              {testimonials.map((testimonial) => (
                <div key={testimonial.id} className="flex-[0_0_100%] min-w-0 px-4">
                  <Card className="border-2">
                    <CardContent className="pt-6">
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <Users className="text-amber-600" size={24} />
                        </div>
                        <div>
                          <p className="text-gray-700 mb-4 italic text-sm">"{testimonial.content}"</p>
                          <div>
                            <div className="font-bold text-gray-900">{testimonial.name}</div>
                            <div className="text-sm text-gray-600">{testimonial.role}</div>
                            {testimonial.company && (
                              <div className="text-sm text-amber-600 font-medium">{testimonial.company}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gradient-to-br from-amber-600 to-amber-700 text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Let's turn your content into a lead generation engine.
          </h2>
          <p className="text-xl mb-10 opacity-90">
            Reach out directly for strategy, content systems, or optimization.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <a href="mailto:thepratishjain@gmail.com">
              <Button
                size="lg"
                className="bg-white text-amber-600 hover:bg-gray-100 shadow-lg hover:shadow-xl transition-all"
              >
                thepratishjain@gmail.com
              </Button>
            </a>
            <a
              href="https://www.linkedin.com/in/pratishjain/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                size="lg"
                variant="outline"
                className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-amber-600 transition-all"
              >
                Connect on LinkedIn
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
